/**
 * Custom storage adapter for Supabase client
 * Handles cookie parsing errors gracefully and prevents console spam
 */

import { safeParseCookie, encodeCookieValue } from '@/lib/cookie-utils'

interface StorageAdapter {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

/**
 * Custom storage adapter that handles cookie parsing errors gracefully
 */
export class SafeClientStorage implements StorageAdapter {
  private storage: Storage
  private prefix: string

  constructor(storage: Storage = typeof window !== 'undefined' ? window.localStorage : null, prefix = 'sb-') {
    if (!storage) {
      // Fallback to memory storage for SSR
      this.storage = createMemoryStorage()
    } else {
      this.storage = storage
    }
    this.prefix = prefix
  }

  getItem(key: string): string | null {
    try {
      const fullKey = `${this.prefix}${key}`
      const value = this.storage.getItem(fullKey)

      if (!value) return null

      // Only try to parse special cookie formats that aren't already strings for Supabase
      // Supabase expects specific formats, so only parse when necessary
      if (value.startsWith('base64-')) {
        // Check if it looks like a malformed cookie value
        const afterPrefix = value.substring(7)
        if (afterPrefix.startsWith('eyJ')) {
          // This is likely "base64-eyJ..." which is a double-encoded value
          // Just return the base64 encoded part
          return afterPrefix
        }

        // Otherwise, try normal parsing
        const parsed = safeParseCookie(value)
        if (parsed && typeof parsed === 'object' && parsed !== null) {
          return JSON.stringify(parsed)
        }
        return parsed || value
      }

      return value
    } catch (error) {
      // Silently handle storage errors
      if (process.env.NODE_ENV === 'development' && process.env.DEBUG_STORAGE) {
        console.debug(`Storage read error for key ${key}:`, error)
      }
      return null
    }
  }

  setItem(key: string, value: string): void {
    try {
      const fullKey = `${this.prefix}${key}`
      
      // Try to parse the value to see if it's JSON
      try {
        const parsed = JSON.parse(value)
        // If it's an object, encode it properly
        if (typeof parsed === 'object' && parsed !== null) {
          value = encodeCookieValue(parsed)
        }
      } catch {
        // Not JSON, store as-is
      }
      
      this.storage.setItem(fullKey, value)
    } catch (error) {
      // Silently handle storage errors
      if (process.env.NODE_ENV === 'development' && process.env.DEBUG_STORAGE) {
        console.debug(`Storage write error for key ${key}:`, error)
      }
    }
  }

  removeItem(key: string): void {
    try {
      const fullKey = `${this.prefix}${key}`
      this.storage.removeItem(fullKey)
    } catch (error) {
      // Silently handle storage errors
      if (process.env.NODE_ENV === 'development' && process.env.DEBUG_STORAGE) {
        console.debug(`Storage remove error for key ${key}:`, error)
      }
    }
  }

  /**
   * Clear all items with the storage prefix
   */
  clear(): void {
    try {
      const keysToRemove: string[] = []
      
      // Collect all keys with our prefix
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i)
        if (key && key.startsWith(this.prefix)) {
          keysToRemove.push(key)
        }
      }
      
      // Remove collected keys
      keysToRemove.forEach(key => {
        this.storage.removeItem(key)
      })
    } catch (error) {
      if (process.env.NODE_ENV === 'development' && process.env.DEBUG_STORAGE) {
        console.debug('Storage clear error:', error)
      }
    }
  }
}

/**
 * Create a memory-based storage for SSR environments
 */
function createMemoryStorage(): Storage {
  const store = new Map<string, string>()
  
  return {
    getItem: (key: string) => store.get(key) || null,
    setItem: (key: string, value: string) => { store.set(key, value) },
    removeItem: (key: string) => { store.delete(key) },
    clear: () => { store.clear() },
    get length() { return store.size },
    key: (index: number) => {
      const keys = Array.from(store.keys())
      return keys[index] || null
    }
  }
}

/**
 * Get the appropriate storage adapter for the current environment
 */
export function getClientStorage(): SafeClientStorage {
  if (typeof window !== 'undefined') {
    return new SafeClientStorage(window.localStorage)
  }
  return new SafeClientStorage()
}

/**
 * Create a custom storage configuration for Supabase client
 */
export function createSupabaseStorageConfig() {
  const storage = getClientStorage()
  
  return {
    storage: {
      getItem: async (key: string) => storage.getItem(key),
      setItem: async (key: string, value: string) => storage.setItem(key, value),
      removeItem: async (key: string) => storage.removeItem(key),
    },
    // Don't log cookie parsing errors
    debug: false,
  }
}